import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperience,
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import {
  Schedule,
  ScheduleStatus,
  ScheduleType
} from '@dadohr/shared/domainTypes/schedules';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { compact, groupBy, mapValues, orderBy, sortBy } from 'lodash';
import moment from 'moment-timezone';
import {
  getExperiencesByIdAsMap,
  getParticipantActor,
  getUserActorForKey
} from '../../../services/experiences/instance';
import { getExperienceTemplatesAsMap } from '../../../services/experiences/template';
import { store } from '../../../services/firebase';
import {
  CollectionListener,
  createSingleCollectionListenerStore,
  useCollectionListener
} from '../../../services/firecache/collectionListener';
import { getOrg } from '../../../services/orgs';
import { toScheduleDoc } from '../../../services/schedules/documents';
import { fromMoment, Timestamp } from '../../../services/time';
import { getUsersAsMap, toFullName } from '../../../services/user';
import { getAllOrgIds, isDemoCompany } from '../orgs';

const schedulesCollection = () => store().collection(FS.schedules);
const getSchedulesListener = createSingleCollectionListenerStore(
  () => new CollectionListener(schedulesCollection(), toScheduleDoc)
);

export const useAllSchedules = () => {
  return useCollectionListener(getSchedulesListener());
};

export const getAllDueSchedulesInOrg = (orgId: string, n: Timestamp) => {
  return schedulesCollection()
    .where('orgId', '==', orgId)
    .where('at', '<=', n)
    .where('status', 'in', [ScheduleStatus.PENDING, ScheduleStatus.RETRY])
    .get()
    .then((s) => s.docs.map(toScheduleDoc));
};

export const getAllDueSchedulesInOneExecutionWindowInOrg = (
  orgId: string,
  t: { start: Timestamp; end: Timestamp }
) => {
  return schedulesCollection()
    .where('orgId', '==', orgId)
    .where('at', '>=', t.start)
    .where('at', '<', t.end)
    .where('status', 'in', [ScheduleStatus.PENDING, ScheduleStatus.RETRY])
    .get()
    .then((s) => s.docs.map(toScheduleDoc));
};

export type SchedulesOverview = {
  org: Doc<IOrganization>;
  templates: {
    tpl: Doc<IExperienceTemplate>;
    experiences: {
      exp: Doc<IExperience>;
      participant: Doc<IUser>;
      schedulesByType: {
        type: ScheduleType;
        ss: Doc<Schedule>[];
      }[];
    }[];
  }[];
};

export const getSchedulesOverview = async (
  orgIds: string[],
  window: { start: Timestamp; end: Timestamp },
  opts: {
    excludeEmpty: boolean;
  }
) => {
  const data: SchedulesOverview[] = [];
  await Promise.all(
    orgIds.map(async (orgId) => {
      const org = await getOrg(orgId);
      if (!org) {
        return;
      }
      // decide whether to split the immediate ones out right away.
      // might apply a filter param
      // might in general take a time param, so that a user is able to page few all future
      // activity
      // const schedules = await getAllDueSchedulesInOrg(orgId, n);
      const schedules = await getAllDueSchedulesInOneExecutionWindowInOrg(
        orgId,
        window
      );
      console.log('schedules', schedules.length, schedules);
      const templatesToRetrieve = new Set<string>();
      const expsToRetrieve = new Set<string>();
      const usersToRetrieve = new Set<string>();
      const grouped = mapValues(
        groupBy(schedules, (s1) => {
          templatesToRetrieve.add(s1.data.templateId);
          return s1.data.templateId;
        }),
        (byTemplate) => {
          return mapValues(
            groupBy(byTemplate, (s2) => {
              expsToRetrieve.add(s2.data.experienceId);
              return s2.data.experienceId;
            }),
            (ss) => {
              return groupBy(ss, (s3) => s3.data.type);
            }
          );
        }
      );
      const tpls = await getExperienceTemplatesAsMap([...templatesToRetrieve]);
      const exps = await getExperiencesByIdAsMap([...expsToRetrieve]);

      const c1: {
        org: Doc<IOrganization>;
        templates: {
          tpl: Doc<IExperienceTemplate>;
          experiences: {
            exp: Doc<IExperience>;
            schedulesByType: {
              type: ScheduleType;
              ss: Doc<Schedule>[];
            }[];
          }[];
        }[];
      } = {
        org,
        templates: []
      };

      Object.entries(grouped).forEach(([tplId, byTpl]) => {
        const tpl = tpls[tplId];
        if (!tpl) {
          // handle error
          console.log('NO_TEMPLATE', { tplId });
          return;
        }
        const c2: {
          tpl: Doc<IExperienceTemplate>;
          experiences: {
            exp: Doc<IExperience>;
            schedulesByType: {
              type: ScheduleType;
              ss: Doc<Schedule>[];
            }[];
          }[];
        } = {
          tpl,
          experiences: []
        };
        c1.templates.push(c2);
        Object.entries(byTpl).forEach(([expId, byExp]) => {
          const exp = exps[expId];
          if (!exp) {
            console.log('NO_EXPERIENCE', { expId });
            return;
          }
          const participantActor = getParticipantActor(exp);
          if (!participantActor) {
            // handle error
            console.log('NO_PARTICIPANT_ACTOR', { exp });
            return;
          }
          usersToRetrieve.add(participantActor.userId);

          // TODO take secondary ids into account to highlight channeling

          const c3: {
            exp: Doc<IExperience>;
            schedulesByType: {
              type: ScheduleType;
              ss: Doc<Schedule>[];
            }[];
          } = {
            exp,
            schedulesByType: []
          };

          c2.experiences.push(c3);

          Object.entries(byExp).forEach(([type, ss]) => {
            c3.schedulesByType.push({
              type: type as ScheduleType,
              ss
            });
          });
        });
      });
      const users = await getUsersAsMap([...usersToRetrieve]);
      const finalDataForOrg: SchedulesOverview = {
        org: c1.org,
        templates: c1.templates.map((x) => {
          return {
            ...x,
            experiences: orderBy(
              compact(
                x.experiences.map((e) => {
                  const participantActor = getUserActorForKey(
                    e.exp,
                    PARTICIPANT_ACTOR_KEY
                  );
                  if (participantActor) {
                    const user = users[participantActor.userId];
                    if (user) {
                      return {
                        ...e,
                        participant: user,
                        schedulesByType: sortBy(
                          e.schedulesByType,
                          ({ type }) => {
                            switch (type) {
                              case ScheduleType.EXPERIENCE_START:
                                return 0;
                              case ScheduleType.EXPERIENCE_MESSAGE:
                                return 1;
                              case ScheduleType.TASK_START:
                                return 2;
                              case ScheduleType.TASK_REMINDER:
                                return 3;
                              case ScheduleType.TASK_LATE_REMINDER:
                                return 4;
                              default:
                                return 5;
                            }
                          }
                        )
                      };
                    }
                  }
                  console.log('NO_PARTICIPANT', { exp: e.exp });
                  return null;
                })
              ),
              [
                (e) => e.exp.data.startsAt.toMillis(),
                (e) => toFullName(e.participant.data)
              ]
            )
          };
        })
      };
      if (opts.excludeEmpty && !finalDataForOrg.templates.length) {
        return;
      }
      data.push(finalDataForOrg);
    })
  );
  return sortBy(data, (d) => d.org.data.name);
};

let DEV_CACHE: SchedulesOverview[];

export const getSchedulesOverviewForAllOrgs = async (window: {
  start: Timestamp;
  end: Timestamp;
}) => {
  const orgIds = await getAllOrgIds().then((xs) =>
    xs.filter((x) => !isDemoCompany(x))
  );
  return getSchedulesOverview(orgIds, window, {
    excludeEmpty: true
  });
};

export const getSchedulesOverviewWithDevCache = async () => {
  if (!DEV_CACHE) {
    DEV_CACHE = await getSchedulesOverviewForAllOrgs({
      start: fromMoment(moment()),
      end: fromMoment(moment().add(1, 'h'))
    });
  }
  return DEV_CACHE;
};
